import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Login from "@/pages/Login";
import DriverDashboard from "@/pages/DriverDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import MechanicDashboard from "@/pages/MechanicDashboard";
import OperatorDashboard from "@/pages/OperatorDashboard";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import axios from "axios";

// Configure axios to prevent caching
axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
axios.defaults.headers.common['Pragma'] = 'no-cache';
axios.defaults.headers.common['Expires'] = '0';

// Add timeout for slow mobile connections
axios.defaults.timeout = 30000;

// Add timestamp to all GET requests to bust cache
axios.interceptors.request.use((config) => {
  if (config.method === 'get') {
    config.params = {
      ...config.params,
      _t: Date.now()
    };
  }
  return config;
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Safe localStorage wrapper for Android compatibility
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error("localStorage read error:", e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.error("localStorage write error:", e);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error("localStorage remove error:", e);
    }
  }
};

// Helper to decode JWT and check expiration
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp;
  } catch (e) {
    return true;
  }
};

// Get time until token expires (in ms)
const getTimeUntilExpiry = (token) => {
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;
    return Math.max(0, exp - Date.now());
  } catch (e) {
    return 0;
  }
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    safeStorage.removeItem("token");
    safeStorage.removeItem("user");
    safeStorage.removeItem("loginTime");
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  useEffect(() => {
    const token = safeStorage.getItem("token");
    const userData = safeStorage.getItem("user");
    
    if (token && userData) {
      // Check if token is expired
      if (isTokenExpired(token)) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        try {
          setUser(JSON.parse(userData));
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Set up auto-logout when token expires
          const timeUntilExpiry = getTimeUntilExpiry(token);
          if (timeUntilExpiry > 0) {
            const logoutTimer = setTimeout(() => {
              toast.error("Session expired. Please login again.");
              handleLogout();
            }, timeUntilExpiry);
            
            // Cleanup timer on unmount
            setLoading(false);
            return () => clearTimeout(logoutTimer);
          }
        } catch (e) {
          console.error("Error parsing user data:", e);
          handleLogout();
        }
      }
    }
    setLoading(false);
  }, [handleLogout]);

  // Also check token on every API response
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          toast.error("Session expired. Please login again.");
          handleLogout();
        }
        return Promise.reject(error);
      }
    );
    
    return () => axios.interceptors.response.eject(interceptor);
  }, [handleLogout]);

  const handleLogin = (token, userData) => {
    const tokenSaved = safeStorage.setItem("token", token);
    const userSaved = safeStorage.setItem("user", JSON.stringify(userData));
    safeStorage.setItem("loginTime", Date.now().toString());
    
    if (!tokenSaved || !userSaved) {
      toast.error("Unable to save login. Please enable cookies/storage.");
      return;
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    
    // Set up auto-logout timer
    const timeUntilExpiry = getTimeUntilExpiry(token);
    if (timeUntilExpiry > 0) {
      setTimeout(() => {
        toast.error("Session expired. Please login again.");
        handleLogout();
      }, timeUntilExpiry);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-lg font-mono">Loading...</div>
      </div>
    );
  }

  // Helper function to determine redirect path based on user role
  const getRedirectPath = (userRole) => {
    switch (userRole) {
      case 'admin':
      case 'manager':
        return '/admin';
      case 'mechanic':
        return '/mechanic';
      case 'operator':
        return '/operator';
      case 'driver':
      default:
        return '/driver';
    }
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to={getRedirectPath(user.role)} replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/driver"
            element={
              user && user.role === "driver" ? (
                <DriverDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/mechanic"
            element={
              user && user.role === "mechanic" ? (
                <MechanicDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/operator"
            element={
              user && user.role === "operator" ? (
                <OperatorDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <AdminDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                <Navigate to={getRedirectPath(user.role)} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;